import { supabase } from './supabase';

export const resultsApi = {
  // === USER RESULTS ===

  // Uložit výsledek testu/simulace
  async saveResult(userId, resultData) {
    const { data, error } = await supabase
      .from('user_results')
      .insert({
        user_id: userId,
        type: resultData.type, // 'quiz' nebo 'simulation'
        category: resultData.category || null, // pro quiz
        score: resultData.score,
        total_questions: resultData.totalQuestions,
        percentage: resultData.percentage,
        time_seconds: resultData.timeSeconds,
        answers: resultData.answers, // JSON pole s detaily odpovědí
        total_score: resultData.totalScore || null, // dosažené body (pro simulaci)
        max_score: resultData.maxScore || null // maximální body (pro simulaci)
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Načíst všechny výsledky uživatele (max 12 měsíců staré)
  async getUserResults(userId, type = null) {
    // Automaticky smazat výsledky starší než 12 měsíců
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    // Smazat staré výsledky (tiše, bez čekání)
    supabase
      .from('user_results')
      .delete()
      .eq('user_id', userId)
      .lt('created_at', twelveMonthsAgo.toISOString())
      .then(() => {});

    // Načíst výsledky (pouze z posledních 12 měsíců)
    let query = supabase
      .from('user_results')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', twelveMonthsAgo.toISOString())
      .order('created_at', { ascending: false });

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Načíst detail jednoho výsledku
  async getResultById(resultId) {
    const { data, error } = await supabase
      .from('user_results')
      .select('*')
      .eq('id', resultId)
      .single();

    if (error) throw error;
    return data;
  },

  // Smazat výsledek
  async deleteResult(resultId, userId) {
    const { error } = await supabase
      .from('user_results')
      .delete()
      .eq('id', resultId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  // Statistiky uživatele
  async getUserStats(userId) {
    const { data, error } = await supabase
      .from('user_results')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    if (!data || data.length === 0) {
      return {
        totalTests: 0,
        totalQuestions: 0,
        totalCorrect: 0,
        averagePercentage: 0,
        totalTime: 0,
        byCategory: {},
        byType: { quiz: [], simulation: [] },
        weakCategories: [],
        strongCategories: []
      };
    }

    // Výpočet statistik
    const totalTests = data.length;
    const totalQuestions = data.reduce((sum, r) => sum + r.total_questions, 0);
    const totalCorrect = data.reduce((sum, r) => sum + r.score, 0);
    const averagePercentage = Math.round(totalCorrect / totalQuestions * 100);
    const totalTime = data.reduce((sum, r) => sum + (r.time_seconds || 0), 0);

    // Statistiky podle kategorie
    const byCategory = {};
    data.forEach(result => {
      const cat = result.category || 'Simulace';
      if (!byCategory[cat]) {
        byCategory[cat] = { tests: 0, correct: 0, total: 0, totalTime: 0 };
      }
      byCategory[cat].tests += 1;
      byCategory[cat].correct += result.score;
      byCategory[cat].total += result.total_questions;
      byCategory[cat].totalTime += result.time_seconds || 0;
    });

    // Vypočítat průměr pro každou kategorii
    Object.keys(byCategory).forEach(cat => {
      byCategory[cat].percentage = Math.round(byCategory[cat].correct / byCategory[cat].total * 100);
    });

    // Seřadit kategorie podle úspěšnosti
    const sortedCategories = Object.entries(byCategory)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => a.percentage - b.percentage);

    const weakCategories = sortedCategories.filter(c => c.percentage < 70).slice(0, 3);
    const strongCategories = sortedCategories.filter(c => c.percentage >= 70).sort((a, b) => b.percentage - a.percentage).slice(0, 3);

    return {
      totalTests,
      totalQuestions,
      totalCorrect,
      averagePercentage,
      totalTime,
      byCategory,
      byType: {
        quiz: data.filter(r => r.type === 'quiz'),
        simulation: data.filter(r => r.type === 'simulation')
      },
      weakCategories,
      strongCategories
    };
  },

  // === FAVORITES ===

  // Přidat do oblíbených
  async addFavorite(userId, questionId) {
    const { data, error } = await supabase
      .from('user_favorites')
      .insert({
        user_id: userId,
        question_id: questionId
      })
      .select()
      .single();

    if (error) {
      // Pokud už existuje, ignorovat
      if (error.code === '23505') return null;
      throw error;
    }
    return data;
  },

  // Odebrat z oblíbených
  async removeFavorite(userId, questionId) {
    const { error } = await supabase
      .from('user_favorites')
      .delete()
      .eq('user_id', userId)
      .eq('question_id', questionId);

    if (error) throw error;
  },

  // Načíst oblíbené otázky
  async getFavorites(userId, category = null) {
    let query = supabase
      .from('user_favorites')
      .select(`
        id,
        created_at,
        question:questions(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    // Filtrovat podle kategorie pokud je zadána
    if (category && data) {
      return data.filter(f => f.question?.category === category);
    }

    return data;
  },

  // Zkontrolovat jestli je otázka v oblíbených
  async isFavorite(userId, questionId) {
    const { data, error } = await supabase
      .from('user_favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('question_id', questionId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  },

  // Načíst ID všech oblíbených otázek (pro rychlou kontrolu)
  async getFavoriteIds(userId) {
    const { data, error } = await supabase
      .from('user_favorites')
      .select('question_id')
      .eq('user_id', userId);

    if (error) throw error;
    return new Set(data.map(f => f.question_id));
  }
};
